import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Game, RoundGroup, Round, GameValidation, EditPass, Tag, GameTemplate } from '../models/game.model';
import { Media } from '../models/game.model';
import {
  EditTextRequest,
  CreateGameRequest,
  EditGameRequest,
  EditGroupRequest,
  CreateGroupRequest,
  CreateOrEditRoundRequest,
  EditRoundsOrderRequest,
  CreateLocalizationRequest,
  DeleteLocalizationRequest,
  createAIGameJobRequest
} from '../models/request.model';
import { Balance } from '../models/auth.model';
import { TagService } from '../services/tag.service';
// import { UpdateGameTagsRequest } from '../models/tag.model';

@Injectable({
  providedIn: 'root'
})
export class GameBuilderService {
  private apiUrl = `${environment.apiUrl}/game-builder`;

  private tagService = inject(TagService);

  constructor(private http: HttpClient) { }

  getMyGames() {
    return this.http.get<Game[]>(`${this.apiUrl}/games`);
  }

  getMyGame(slug: string, lang: string) {
    return this.http.get<{game: Game, validation: GameValidation}>(`${this.apiUrl}/games/${slug}`, { params: { lang } });
  }

  editText(request: EditTextRequest) {
    return this.http.patch(`${this.apiUrl}/texts`, request);
  }

  getMyMedia() {
    return this.http.get<Media[]>(`${this.apiUrl}/media`);
  }

  uploadMedia(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Media>(`${this.apiUrl}/media`, formData);
  }

  replaceMedia(file: File, id: number) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put<Media>(`${this.apiUrl}/media/${id}/file`, formData);
  }

  editMediaName(id: number, name: string) {
    return this.http.patch<Media>(`${this.apiUrl}/media/${id}/name`, { name });
  }

  removeMedia(id: number) {
    return this.http.delete(`${this.apiUrl}/media/${id}`);
  }

  editGameStatus(id: number, status: string) {
    return this.http.patch<{game: Game, validation: GameValidation}>(`${this.apiUrl}/games/${id}/status`, { status });
  }

  createGame(game: CreateGameRequest) {
    return this.http.post<{ createdGame: Game, balance: Balance }>(`${this.apiUrl}/games`, game);
  }

  validateGame(id: number) {
    return this.http.get<GameValidation>(`${this.apiUrl}/games/${id}/validation`);
  }

  editGame(editGameRequest: EditGameRequest) {
    return this.http.put<Game>(`${this.apiUrl}/games/${editGameRequest.id}`, editGameRequest);
  }

  deleteGame(id: number) {
    return this.http.delete<{result: boolean}>(`${this.apiUrl}/games/${id}`);
  }

  checkSlugAvailability(slug: string, gameId?: number) {
    let params = new HttpParams().set('slug', slug);
    if (gameId) {
      params = params.set('gameId', gameId);
    }
    return this.http.get<{ available: boolean }>(`${this.apiUrl}/games/slug-available`, { params });
  }

  editGroup(group: EditGroupRequest) {
    return this.http.put<{updatedGroup: RoundGroup, indexMap?: { groupId: number, index: number }[]}>(`${this.apiUrl}/groups/${group.id}`, group);
  }

  createGroup(group: CreateGroupRequest) {
    return this.http.post<{createdGroup: RoundGroup, indexMap?: { groupId: number, index: number }[], validation: GameValidation}>(`${this.apiUrl}/groups`, group);
  }

  deleteGroup(id: number, lang: string) {
    return this.http.delete<{ updatedGame: Game, validation: GameValidation }>(`${this.apiUrl}/groups/${id}`, { body: { lang } });
  }

  editRound(id: number, round: CreateOrEditRoundRequest) {
    return this.http.put<Round>(`${this.apiUrl}/rounds/${id}`, round);
  }

  createRound(round: CreateOrEditRoundRequest) {
    return this.http.post<{updatedGame: Game, validation: GameValidation}>(`${this.apiUrl}/rounds`, round);
  }

  deleteRound(id: number, lang: string) {
    return this.http.delete<{updatedGame: Game, validation: GameValidation}>(`${this.apiUrl}/rounds/${id}`, { body: { lang } });
  }

  groupRounds(group: CreateGroupRequest, roundIds: number[]) {
    return this.http.post<{updatedGame: Game, validation: GameValidation}>(`${this.apiUrl}/group-rounds`, { ...group, roundIds });
  }

  editRoundsOrder(request: EditRoundsOrderRequest) {
    return this.http.patch<{success: boolean, validation?: GameValidation}>(`${this.apiUrl}/rounds/order`, request);
  }

  createLocalization(request: CreateLocalizationRequest) {
    return this.http.post<Game>(`${this.apiUrl}/localizations`, request);
  }

  deleteLocalization(request: DeleteLocalizationRequest) {
    return this.http.delete<{result: boolean}>(`${this.apiUrl}/localizations`, { body: request });
  }

  createEditPass(id: number) {
    return this.http.post<{ editPass: EditPass, balance: Balance }>(`${this.apiUrl}/games/${id}/edit-pass`, {});
  }

  // Tags
  // updateGameTags(request: UpdateGameTagsRequest): Observable<Tag[]> {
  //   return this.tagService.updateGameTags(request);
  // }

  // getGameTags(gameId: number): Observable<Tag[]> {
  //   return this.http.get<Tag[]>(`${environment.apiUrl}/tags/game/${gameId}`);
  // }

  createAIGameJob(request: createAIGameJobRequest) {
    return this.http.post<{jobId: number, status: string}>(`${this.apiUrl}/ai-game-job`, request);
  }

  cancelAIGameJob(jobId: number) {
    return this.http.post<{ok: boolean, already: boolean}>(`${this.apiUrl}/ai-game-job/${jobId}/cancel`, {});
  }

  confirmAIGameJob(jobId: number) {
    return this.http.post<{result: boolean}>(`${this.apiUrl}/ai-game-job/${jobId}/confirm`, {});
  }

  getGameTemplates() {
    return this.http.get<GameTemplate[]>(`${this.apiUrl}/game-templates`);
  }
}
